import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTriangleExclamation,
  faCircleCheck,
  faChevronRight,
  faSpinner,
  faArrowsRotate,
} from "@fortawesome/free-solid-svg-icons"

import { useNavigate } from "react-router-dom"

import { ChangeRow, collapseRemaps, provenanceLink, useDriftQuery } from "features/Provenance"

/**
 * The differences between the questionnaire and the data. Once a project has imported data,
 * checks whether the data's metadata (the uploaded datamap, merged into the DQD) still matches
 * the questionnaire and shows exactly where it differs. Read-only, purely informational. Renders
 * nothing until the tab is active and the project actually has loaded data.
 *
 * Served by the cached drift query: a data import or a checkpoint save invalidates it, so the
 * banner checks again by itself (the Recheck button just refetches).
 */
export const DriftBanner: React.FC<{ projectId: string; isActive: boolean }> = ({
  projectId,
  isActive,
}) => {
  const navigate = useNavigate()
  const openStamp = () => navigate(provenanceLink(projectId, "stamp"))
  const [expanded, setExpanded] = useState(false)

  const {
    data: result,
    refetch,
    isFetching,
  } = useDriftQuery(projectId, isActive)

  // Nothing to show until we have a result for a data-bearing survey project.
  if (!result || !result.hasQnr || !result.hasData) return null

  // Engine couldn't parse one side: say so softly rather than claim they match.
  if (result.parseStatus === "parse_failed") {
    return (
      <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-700" role="status">
        <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 flex-none" />
        <span>Couldn’t compare the data with the questionnaire: one of them couldn’t be read.</span>
        <button type="button" onClick={() => refetch()} className="ml-auto inline-flex items-center gap-1 text-[12px] underline">
          <FontAwesomeIcon icon={isFetching ? faSpinner : faArrowsRotate} className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
          Recheck
        </button>
      </div>
    )
  }

  // As the rows show them: no code changes beside an added, removed or moved answer
  const differences = collapseRemaps(result.drift)
  const n = differences.length

  if (n === 0) {
    return (
      <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700" role="status">
        <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 flex-none" />
        <span>The data matches the questionnaire.</span>
        <div className="ml-auto flex items-center gap-3">
          <button type="button" onClick={openStamp} className="text-[12px] underline">
            View in Provenance
          </button>
          <button type="button" onClick={() => refetch()} className="inline-flex items-center gap-1 text-[12px] underline">
            <FontAwesomeIcon icon={isFetching ? faSpinner : faArrowsRotate} className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
            Recheck
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-3 overflow-hidden rounded-md border border-amber-200 bg-amber-50" role="status">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-amber-800"
      >
        <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 flex-none text-amber-600" />
        <span className="font-medium">
          {n} difference{n === 1 ? "" : "s"} between the questionnaire and the data.
        </span>
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`ml-auto h-3 w-3 flex-none transition-transform ${expanded ? "rotate-90" : ""}`}
        />
      </button>
      {expanded && (
        <div className="border-t border-amber-200 bg-white px-3 py-2">
          <p className="mb-1 text-[11px] text-slate-400">
            Each difference says what the data has compared with the questionnaire.
          </p>
          <ul className="max-h-[320px] overflow-auto">
            {differences.map((c, i) => (
              <ChangeRow key={`${c.path}-${c.kind}-${i}`} change={c} />
            ))}
          </ul>
          <div className="mt-1 flex justify-end gap-3">
            <button type="button" onClick={openStamp} className="text-[12px] text-slate-500 underline">
              View in Provenance
            </button>
            <button type="button" onClick={() => refetch()} className="inline-flex items-center gap-1 text-[12px] text-slate-500 underline">
              <FontAwesomeIcon icon={isFetching ? faSpinner : faArrowsRotate} className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
              Recheck
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DriftBanner
